import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, Subject, filter } from 'rxjs';
import { FilteringHashed, FilteringItem } from '@3ddv/ngx-sites-builder-lib';
import { FilteringItemCustom, FilteringItemPosition, ListAndHash } from '../models/filtersUtils.model';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private filterList: Array<FilteringItem>;
  private filterList$: Subject<Array<FilteringItem>> = new Subject();
  private filterListAsync$: AsyncSubject<Array<FilteringItem>> = new AsyncSubject();
  private filterItemIdToManageAsync$: AsyncSubject<{
    itemId: string;
    itemList: Array<FilteringItem>;
  }> = new AsyncSubject();
  private filterItemIdToManage$: Subject<{
    itemId: string;
    itemList: Array<FilteringItem>;
  }> = new Subject();
  private filterListAndHash$: Subject<ListAndHash> = new Subject();
  constructor() {}

  public getListAndHash$(): Observable<ListAndHash> {
    return this.filterListAndHash$.asObservable();
  }
  public setListAndHash$(listandhash: ListAndHash) {
    this.filterListAndHash$.next(listandhash);
  }
  public getFilteringListActions$(): Observable<Array<FilteringItem>> {
    return this.filterList$.asObservable();
  }
  public getFilteringListActionsAsync$(): Observable<Array<FilteringItem>> {
    return this.filterListAsync$.asObservable();
  }
  public setFilteringListActionsAsync$(filterList?: Array<FilteringItem>) {
    if (filterList && filterList.length) {
      this.filterList = filterList;
    }
    this.filterListAsync$.next(this.filterList);
    this.filterListAsync$.complete();
  }
  public setFilteringListActions(filterList: Array<FilteringItem>) {
    this.filterList = filterList;
    this.filterList$.next(this.filterList);
    this.setFilteringListActionsAsync$();
  }

  public getFilterinItemToManage$(): Observable<{ itemId: string; itemList }> {
    return this.filterItemIdToManage$.asObservable();
  }
  public getFilterinItemToManageAsync$(): Observable<{
    itemId: string;
    itemList;
  }> {
    return this.filterItemIdToManageAsync$.asObservable();
  }

  public setFilterinItemToManage$(filterId?: string, itemList?: Array<FilteringItem>) {
    this.filterItemIdToManage$.next({ itemId: filterId, itemList });
  }
  public setFilterinItemToManageAsync$(filterId?: string, itemList?: Array<FilteringItem>) {
    this.filterItemIdToManageAsync$.next({ itemId: filterId, itemList });
    this.filterItemIdToManageAsync$.complete();
  }

  public generateFilenameToDownload() {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    const yyyy = today.getFullYear();
    const time = today.getHours().toString() + today.getMinutes().toString() + today.getSeconds().toString();
    return 'filtering_' + time + '_' + mm + dd + yyyy + '.json';
  }

  public itemListToHashDownload(itemList: Array<FilteringItem>, result: any, parentId?: string): FilteringHashed {
    for (let i = 0; i < itemList.length; i++) {
      const item = itemList[i];
      const parsed = {
        id: item.id,
        name: item.name,
        nodes: {
          section: item.nodes.section,
          row: item.nodes.row,
          seat: item.nodes.seat,
        },
        mapid: item.mapid,
        venueid: item.venueid,
        parentId: parentId,
        type: item.type,
        color: item.color,
        mode: item.mode ? item.mode : this.getFilterItemMode(item),
      };

      if (item.children.length) {
        this.itemListToHashDownload(item.children, result, item.id);
      }

      result[item.id] = parsed;
    }
    return result;
  }

  private getFilterItemMode(item: FilteringItem) {
    if (item.mode) {
      return item.mode;
    } else if (item.mapid && item.nodes.section.length === 0) {
      return 'view360';
    } else {
      return 'filter';
    }
  }

  public createNewItem(parentItem: FilteringItem): FilteringItemCustom {
    const id = this.generateRandomId();
    return {
      id: id,
      name: '',
      color: parentItem.color,
      children: [],
      nodes: {
        section: [],
        row: [],
        seat: [],
      },
      parentId: parentItem.id,
      mapid: '',
      venueid: '',
      manage: {
        collapsed: false,
        actionsopen: false,
      },
      type: '',
      mode: 'filter',
    };
  }

  public createNewItemParent(): FilteringItem {
    const id = this.generateRandomId();
    return {
      id: id,
      name: '',
      color: '#000',
      children: [],
      nodes: {
        section: [],
        row: [],
        seat: [],
      },
      parentId: '',
      mapid: '',
      venueid: '',
      type: '',
      mode: 'filter',
    };
  }
  private generateRandomId() {
    let result = '';
    const characters = 'ABCD0123';
    const length = 6;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return 'K' + result;
  }

  public parseListToHash(itemList: Array<FilteringItem>, result: any) {
    for (let i = 0; i < itemList.length; i++) {
      const item = itemList[i];
      if (item.children.length) {
        this.parseListToHash(item.children, result);
      }
      result[item.id] = item;
    }
    return result;
  }

  // Elimina el filtro de la lista
  public recursiveDeleteChildren(filter, list) {
    return list
      .map(item => {
        return { ...item };
      })
      .filter(item => {
        if ('children' in item) {
          item.children = this.recursiveDeleteChildren(filter, item.children);
        }
        return item.id !== filter.id;
      });
  }

  public recursiveReplaceChildren(filter, list) {
    return list.map(i => {
      if (i.id === filter.id) {
        i = filter;
      }
      if (i.children && i.children.length) {
        this.recursiveReplaceChildren(filter, i.children);
      }
      return i;
    });
  }
  // Recursive add nodes from hash to list
  public recursiveAddFiltersToList(data, filterlist) {
    for (let i = 0; i < filterlist.length; i++) {
      let item = filterlist[i];
      let id = item.id;
      item.nodes = data[id].nodes;
      item.venueid = data[id].venueid;
      item.mapid = data[id].mapid;
      item.name = data[id].name;
      if (item.children.length) {
        this.recursiveAddFiltersToList(data, item.children);
      }
    }
  }

  public parseHashFileToList(data: FilteringHashed, result: Array<FilteringItem>) {
    console.log(data);
    return result;
  }
  public recursiveReOrderFilter(move: FilteringItemPosition, filterList: Array<FilteringItem>) {
    filterList.map(i => {
      if (i.children && i.children.length) {
        let found = i.children.filter(i => i.id === move.id);
        if (found.length) {
          let new_pos = 0;
          if (move.action === 'up') {
            new_pos = move.currentPosition - 1;
          } else if (move.action === 'down') {
            new_pos = move.currentPosition + 1;
          }
          const save_item_newpos = i.children[new_pos];
          i.children[new_pos] = found[0];
          i.children[move.currentPosition] = save_item_newpos;
        } else {
          //if not found recursive
          this.recursiveReOrderFilter(move, i.children);
        }
      }
    });
  }
  // Recurse set color to all childrens
  public recursiveSetColor(color: string, itemList: Array<FilteringItem>) {
    return itemList.map(i => {
      i.color = color;

      if (i.children.length) {
        this.recursiveSetColor(color, i.children);
      }

      return i;
    });
  }

  public recursiveRemoveFromListKey(key: string, list: Array<FilteringItem>) {
    return list.map(i => {
      if (i[key]) {
        delete i[key];
      }
      if (i.children && i.children.length) {
        this.recursiveRemoveFromListKey(key, i.children);
      }
      return i;
    });
  }

  public removeKeyFromObj(key: string, obj: FilteringHashed) {
    let keys = Object.keys(obj);
    keys.forEach(k => {
      if (obj[k][key]) delete obj[k][key];
    });
    return obj;
  }

  private recursiveGetTreePositions(itemList: Array<FilteringItem>, treePositions: { [key: string]: number }) {
    if (itemList.length) {
      itemList.forEach((node, index) => {
        if (node) {
          treePositions[node.id] = index;
          if (node.children.length) {
            treePositions = this.recursiveGetTreePositions(node.children, treePositions);
          }
        }
      });
    }
    return treePositions;
  }

  private recursiveCleanList(itemList: Array<FilteringItem>) {
    itemList = itemList.filter(item => item);
    itemList.forEach(item => {
      if (item.children.length) {
        item.children = this.recursiveCleanList(item.children);
      }
    });
    return itemList;
  }

  public parseHashToList(itemListHashed: FilteringHashed, currentItemList: Array<FilteringItem>) {
    const copyObj = Object.assign({}, itemListHashed);
    const currentItemHashList = Object.values(copyObj) as FilteringItem[];
    const map = {};
    let newItemList = [];

    // Generate new list from hash
    // Map the id of each element to its node and initialize an empty `children` array
    currentItemHashList.forEach(node => {
      node.children = [];
      map[node.id] = node;
    });
    // For each element, add it as a child of its parent node
    currentItemHashList.forEach(node => {
      const parent = map[node.parentId];
      if (parent) {
        parent.children.push(node);
      } else {
        newItemList.push(node);
      }
    });

    // Keep order on edit item
    const treePositions = this.recursiveGetTreePositions(currentItemList, {});
    newItemList = this.recursiveKeepExistingOrder(newItemList, treePositions, currentItemList, []);
    // Clean possible empty slots
    newItemList = this.recursiveCleanList(JSON.parse(JSON.stringify(newItemList)));
    return newItemList;
  }

  private recursiveKeepExistingOrder(
    itemList: Array<FilteringItem>,
    treePositions: { [key: string]: number },
    oldItemList: Array<FilteringItem>,
    result: Array<FilteringItem>
  ) {
    itemList.forEach((node, index) => {
      let position = treePositions[node.id];

      if (typeof position === 'number') {
        if (oldItemList[position] && oldItemList[position].id === node.id) {
          if (result[position] && result[position].id !== node.id) {
            result.push(node);
          } else {
            result[position] = node;
          }
        } else {
          result.push(node);
        }
      } else {
        result.push(node);
      }
      if (node.children.length && oldItemList[position] && result[position]) {
        node.children = this.recursiveKeepExistingOrder(
          node.children,
          treePositions,
          oldItemList[position].children,
          []
        );
      }
    });
    return result;
  }

  public parseTextareaToArray(str: string): Array<string> {
    // remove specialcharacters
    str = str.replace(/[^a-zA-Z0-9-_,()]/g, '');
    const arr = str.split(',').filter(n => n);
    return arr;
    /*     let replaced = str
      .replace(/[^a-zA-Z0-9-_ ]/g, '') // replace caracteres especiales
      .replace(/ /g, '\n') // replace espacio
      .replace(/,/g, '\n') // replace coma
      .replace(/\r\n/g, '\n') // replace salto de carro
      .split('\n') // convierte en array
      .filter(n => n); // se borran vacios
    console.log(replaced);
    return replaced; */
  }

  public parseArrayToString(arr: Array<string>): string {
    let raw = JSON.stringify(arr);
    raw = raw.split('[').join('');
    raw = raw.split('"').join('');
    raw = raw.split(']').join('');
    raw = raw.split(',').join(', ');
    return raw;
  }

  public recursiveGetChildrenIds(filterId: string, list: Array<FilteringItem>, result: Array<string>) {
    list.forEach(i => {
      if ((i.id === filterId || !filterId) && i.children && i.children.length) {
        result = result.concat(i.children.map(i => i.id));
        result = this.recursiveGetChildrenIds(null, i.children, result);
      }
    });
    return result;
  }
}
